import axios from 'axios';
import Cookies from 'js-cookie';

// Check environment
const isDevelopment = process.env.NODE_ENV === 'development';

// Use proxy path in development, direct URL in production
const BASE_URL = isDevelopment ? '/public/api/v1' : 'https://api.tazatropics.com/public/api/v1';
const MEDIA_URL = 'https://api.tazatropics.com/public/storage/images';

// Configure axios defaults
axios.defaults.headers.common['Accept'] = 'application/json';

// Helper to convert object to URLSearchParams - matching Pure Meat's JSON_to_URLEncoded
const toFormData = (element, key = null, list = []) => {
  if (typeof element === 'object' && element !== null && !(element instanceof Date)) {
    for (let idx in element) {
      toFormData(
        element[idx],
        key ? `${key}[${idx}]` : idx,
        list
      );
    }
  } else {
    list.push(`${key}=${encodeURIComponent(element)}`);
  }
  return list.join('&');
};

// Send verification email for registration
export const sendVerificationOnMail = async (email, subject = 'Verification', country_code = '', mobile = '') => {
  try {
    const param = {
      email,
      subject,
      header_text: 'Use this code for verification',
      thank_you_text: "Don't share this otp to anybody else",
      mediaURL: MEDIA_URL,
      country_code,
      mobile
    };

    console.log('Sending verification email with params:', param);

    const formBody = toFormData(param);
    console.log('Encoded form body:', formBody);

    const response = await axios.post(`${BASE_URL}/sendVerificationOnMail`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    const data = response.data;
    console.log('sendVerificationOnMail response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data === true && data.otp_id) {
      return { success: true, otp_id: data.otp_id };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to send verification email'
      };
    }
  } catch (error) {
    console.error('sendVerificationOnMail error:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);
    return { 
      success: false, 
      message: error.response?.data?.message || error.response?.data?.error || 'Network error. Please try again.',
      errors: error.response?.data?.errors || error.response?.data
    };
  }
};

// Register new user
export const registerUser = async (userData) => {
  try {
    console.log('Registration data being sent:', userData);
    
    const formBody = toFormData(userData);
    
    const response = await axios.post(`${BASE_URL}/auth/create_account`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    const data = response.data;
    console.log('Registration response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true, data: data.user, token: data.token };
    } else {
      return { 
        success: false, 
        message: data.message || 'Registration failed',
        errors: data.errors 
      };
    }
  } catch (error) {
    console.error('Register API error:', error);
    return { success: false, message: error.response?.data?.message || 'Network error. Please try again.' };
  }
};

// Login user with email or mobile
export const loginUser = async (credentials) => {
  try {
    const formBody = toFormData(credentials);
    
    const response = await axios.post(`${BASE_URL}/auth/login`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    const data = response.data;
    
    if (response.status === 200 && data.status === 200) {
      // Store token in cookies for private API usage
      if (data.token) {
        Cookies.set('authToken', data.token, { expires: 7 }); // expires in 7 days
      }
      return { success: true, data: data.user, token: data.token };
    } else {
      return { 
        success: false, 
        message: data.message || 'Login failed'
      };
    }
  } catch (error) {
    console.error('Login API error:', error);
    return { success: false, message: error.response?.data?.message || 'Network error. Please try again.' };
  }
};

// Logout user
export const logoutUser = async (userId, token) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      },
      body: toFormData({ id: userId })
    });

    const data = await response.json();
    
    if (response.ok && data.status === 200) {
      return { success: true };
    } else {
      return { success: false, message: data.message || 'Logout failed' };
    }
  } catch (error) {
    console.error('Logout API error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
};

// Verify OTP for registration
export const verifyOTP = async (otpId, otp) => {
  try {
    const formBody = toFormData({ id: otpId, otp });
    
    const response = await axios.post(`${BASE_URL}/otp/verifyOTP`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    const data = response.data;
    console.log('verifyOTP response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { success: true, data: data.data };
    } else {
      return { 
        success: false, 
        message: data.message || data.error || 'OTP verification failed'
      };
    }
  } catch (error) {
    console.error('Verify OTP API error:', error);
    return { success: false, message: error.response?.data?.message || error.response?.data?.error || 'Network error. Please try again.' };
  }
};

// Forgot password - sends OTP to email
export const forgotPassword = async (email) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/forgotPassword`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: toFormData({ email })
    });

    const data = await response.json();
    
    if (response.ok && data.status === 200) {
      return { success: true, otp: data.data?.otp };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to send OTP'
      };
    }
  } catch (error) {
    console.error('Forgot password API error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
};

// Reset password after OTP verification
export const resetPassword = async (userId, newPassword) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/reset_password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: toFormData({ id: userId, password: newPassword })
    });

    const data = await response.json();
    
    if (response.ok && data.status === 200) {
      return { success: true };
    } else {
      return { 
        success: false, 
        message: data.message || 'Password reset failed'
      };
    }
  } catch (error) {
    console.error('Reset password API error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
};

// Get user profile by ID
export const getUserProfile = async (userId, token) => {
  try {
    const formBody = toFormData({ id: userId });
    
    const response = await axios.post(`${BASE_URL}/profile/byId`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getUserProfile response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { success: true, data: data.data };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch profile'
      };
    }
  } catch (error) {
    console.error('Get user profile API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.' 
    };
  }
};

// Update user profile
export const updateUserProfile = async (profileData, token) => {
  try {
    const formBody = toFormData(profileData);
    
    const response = await axios.post(`${BASE_URL}/profile/update`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('updateUserProfile response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true, data: data.data };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to update profile',
        errors: data.error || data.errors
      };
    }
  } catch (error) {
    console.error('Update user profile API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      errors: error.response?.data?.error || error.response?.data?.errors
    };
  }
};

// Upload profile image
export const uploadProfileImage = async (file, token) => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await axios.post(`${BASE_URL}/uploadImage`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('uploadProfileImage response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { success: true, image_name: data.data.image_name };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to upload image'
      };
    }
  } catch (error) {
    console.error('Upload profile image API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.' 
    };
  }
};

// Get user orders
export const getUserOrders = async (userId, token, limit = 500000) => {
  try {
    const formBody = toFormData({ id: userId, limit });
    
    const response = await axios.post(`${BASE_URL}/orders/getByUid`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getUserOrders response:', data);
    
    if (response.status === 200 && data.status === 200) {
      const orders = data.data || [];
      // Parse orders JSON for each order
      orders.forEach(order => {
        if (typeof order.orders === 'string') {
          order.orders = JSON.parse(order.orders);
        }
      });
      return { success: true, data: orders };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch orders',
        data: []
      };
    }
  } catch (error) {
    console.error('Get user orders API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// Get user addresses
export const getUserAddresses = async (userId, token) => {
  try {
    const formBody = toFormData({ id: userId });
    
    const response = await axios.post(`${BASE_URL}/address/getByUid`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getUserAddresses response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true, data: data.data || [] };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch addresses',
        data: []
      };
    }
  } catch (error) {
    console.error('Get user addresses API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// Add new address
export const addAddress = async (addressData, token) => {
  try {
    const formBody = toFormData(addressData);
    
    const response = await axios.post(`${BASE_URL}/address/addNew`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('addAddress response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true, data: data.data };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to add address',
        errors: data.error || data.errors
      };
    }
  } catch (error) {
    console.error('Add address API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      errors: error.response?.data?.error || error.response?.data?.errors
    };
  }
};

// Update address
export const updateAddress = async (addressData, token) => {
  try {
    const formBody = toFormData(addressData);
    
    const response = await axios.post(`${BASE_URL}/address/updateMyAddress`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('updateAddress response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true, data: data.data };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to update address',
        errors: data.error || data.errors
      };
    }
  } catch (error) {
    console.error('Update address API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      errors: error.response?.data?.error || error.response?.data?.errors
    };
  }
};

// Delete address
export const deleteAddress = async (addressId, token) => {
  try {
    const formBody = toFormData({ id: addressId });
    
    const response = await axios.post(`${BASE_URL}/address/deleteMyAddress`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('deleteAddress response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to delete address'
      };
    }
  } catch (error) {
    console.error('Delete address API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.' 
    };
  }
};

// ============================================
// OFFERS & COUPONS APIs
// ============================================

// Get available offers/coupons
export const getOffers = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/offers/getMyOffers`);

    const data = response.data;
    console.log('getOffers response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        data: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch offers',
        data: []
      };
    }
  } catch (error) {
    console.error('Get offers API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// ============================================
// WALLET APIs
// ============================================

// Get user wallet balance
export const getWalletBalance = async (userId, token) => {
  try {
    const formBody = toFormData({ id: userId });
    
    const response = await axios.post(`${BASE_URL}/profile/getMyWalletBalance`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getWalletBalance response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        balance: parseFloat(data.data.balance) || 0
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch wallet balance',
        balance: 0
      };
    }
  } catch (error) {
    console.error('Get wallet balance API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      balance: 0
    };
  }
};

// ============================================
// PAYMENT METHODS APIs
// ============================================

// Get available payment methods
export const getPaymentMethods = async (token) => {
  try {
    // If token not provided, get from cookies
    const authToken = token || Cookies.get('authToken');
    if (!authToken) {
      return { success: false, message: 'No auth token found', data: [] };
    }
    const response = await axios.get(`${BASE_URL}/payments/getPayments`, {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });

    const data = response.data;
    console.log('getPaymentMethods response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        data: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch payment methods',
        data: []
      };
    }
  } catch (error) {
    console.error('Get payment methods API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// Send notifications to stores about new order
export const sendNotificationToStores = async (storeIds, orderId, token) => {
  try {
    const formData = new URLSearchParams();
    formData.append('id', storeIds.join(','));
    formData.append('order_id', orderId);
    
    const response = await axios.post(
      `${BASE_URL}/notification/sendToStore`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Send notification error:', error);
    throw error.response?.data || { error: 'Failed to send notifications' };
  }
};

// Send order email to customer
export const sendOrderEmail = async (emailData, token) => {
  try {
    const formData = new URLSearchParams();
    formData.append('id', emailData.id);
    formData.append('mediaURL', emailData.mediaURL);
    formData.append('subject', emailData.subject);
    formData.append('email', emailData.email);
    formData.append('username', emailData.username);
    
    const response = await axios.post(
      `${BASE_URL}/orders/sendMailForOrders`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Send order email error:', error);
    throw error.response?.data || { error: 'Failed to send order email' };
  }
};

// Get Stripe cards for user
export const getStripeCards = async (stripeKey, token) => {
  try {
    const formBody = toFormData({ id: stripeKey });
    
    const response = await axios.post(`${BASE_URL}/payments/getStripeCards`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getStripeCards response:', data);
    
    if (response.status === 200 && data.status === 200 && data.success && data.success.data) {
      return { 
        success: true, 
        data: data.success.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch cards',
        data: []
      };
    }
  } catch (error) {
    console.error('Get Stripe cards API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// Create Stripe payment
export const createStripePayment = async (paymentData, token) => {
  try {
    const formBody = toFormData(paymentData);
    
    const response = await axios.post(`${BASE_URL}/payments/createStripePayments`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('createStripePayment response:', data);
    
    if (response.status === 200 && data.status === 200 && data.success && data.success.id) {
      return { 
        success: true, 
        data: data.success 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Payment failed'
      };
    }
  } catch (error) {
    console.error('Create Stripe payment API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.error?.message || error.response?.data?.message || 'Payment failed. Please try again.'
    };
  }
};

// Get Razorpay key
export const getRazorpayKey = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/getRazorPayKey`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getRazorpayKey response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        key: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch Razorpay key'
      };
    }
  } catch (error) {
    console.error('Get Razorpay key API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.'
    };
  }
};

// Verify Razorpay purchase
export const verifyRazorpayPurchase = async (paymentId, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/payments/VerifyRazorPurchase?id=${paymentId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('verifyRazorpayPurchase response:', data);
    
    if (response.status === 200 && data.status === 200 && data.success && data.success.status === 'captured') {
      return { 
        success: true, 
        data: data.success 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Payment verification failed'
      };
    }
  } catch (error) {
    console.error('Verify Razorpay purchase API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Payment verification failed. Please contact administrator.'
    };
  }
};

// ============================================
// STORES APIs
// ============================================

// Get store data by IDs
export const getStoresData = async (storeIds, token) => {
  try {
    const formBody = toFormData({ id: storeIds });
    
    const response = await axios.post(`${BASE_URL}/stores/getStoresData`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getStoresData response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        data: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch stores',
        data: []
      };
    }
  } catch (error) {
    console.error('Get stores data API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// ============================================
// ORDERS APIs
// ============================================

// Create order
export const createOrder = async (orderData, token) => {
  try {
    console.log('Creating order with data:', orderData);
    
    const formBody = toFormData(orderData);
    
    const response = await axios.post(`${BASE_URL}/orders/create`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('createOrder response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data && data.data.id) {
      return { 
        success: true, 
        data: data.data,
        orderId: data.data.id
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to create order'
      };
    }
  } catch (error) {
    console.error('Create order API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.'
    };
  }
};

// Send notification to stores
export const sendStoreNotification = async (notificationData, token) => {
  try {
    const formBody = toFormData(notificationData);
    
    const response = await axios.post(`${BASE_URL}/notification/sendToStore`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('sendStoreNotification response:', data);
    
    return { success: true };
  } catch (error) {
    console.error('Send store notification API error:', error);
    return { success: false };
  }
};
